import { useActiveAnnouncement } from '@common/components/Announcement/useActiveAnnouncement';
import { FooterV2 } from '@common/components/Footer/FooterV2';
import { useLanguagePickerItems } from '@common/components/LanguageSwitcher/useLanguagePickerItems';
import { Routes } from '@common/constants';
import { useIsMinimalStyle } from '@common/contexts/MinimalStyleContext';
import { getCategoryListRoute } from '@features/Home/SearchFlow';
import { useHomeCategories } from '@features/Home/v2/useHomeCategories';
import { useTranslations } from '@lang';
import { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'shared-routing';
import type {
  AppShellAnnouncementConfig,
  AppShellBannerConfig,
  MenuDrawerItem,
  MobileTabItem,
  SideNavItem,
} from 'voyager';
import { AppShell } from 'voyager';
import { Logo } from './components/Logo';
import type { LayoutProps } from './GeneralLayout';
import { ShellWrapper, TopBar } from './GeneralLayoutV2.styles';
import { VoyagerThemeBridge } from './VoyagerThemeBridge';

export function GeneralLayoutV2({
  children,
  property,
  language,
  basketCount = 0,
  guest,
  termsAndConditionPage = null,
  privacyAndCookiePolicyPage = null,
  cancellationPage = null,
}: LayoutProps) {
  const router = useRouter();
  const { t } = useTranslations();
  const isMinimalStyle = useIsMinimalStyle();

  const isAuthRoute =
    router.pathname === Routes.SIGNIN ||
    router.pathname === Routes.REGISTER ||
    router.pathname === Routes.FORGOTTEN_PASSWORD;

  const isDiningBookingRoute =
    router.pathname === Routes.TABLES_BOOK || router.pathname === Routes.TABLES_BOOK_RESTAURANT;

  const isRoomRateBookingRoute =
    router.pathname === Routes.ROOM_BOOKING || router.pathname === Routes.RATE_BOOKING;

  const hideTopBar =
    isMinimalStyle ||
    router.pathname === Routes.HOME ||
    router.pathname === Routes.SEARCH ||
    router.pathname === Routes.MY_ACCOUNT ||
    router.pathname.startsWith(`${Routes.MY_ACCOUNT}/`) ||
    isAuthRoute ||
    isDiningBookingRoute ||
    isRoomRateBookingRoute;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);

  const goHome = useCallback(() => router.push({ pathname: Routes.HOME }), [router]);
  const goBasket = useCallback(() => router.push({ pathname: Routes.BASKET }), [router]);

  const accountRoute = guest ? Routes.MY_ACCOUNT : Routes.SIGNIN;
  const goAccount = useCallback(
    () => router.push({ pathname: accountRoute }),
    [router, accountRoute]
  );

  const closeMenu = useCallback(() => setIsMenuOpen(false), []);
  const toggleMenu = useCallback(
    () =>
      setIsMenuOpen((v) => {
        if (!v) setIsLanguageOpen(false);
        return !v;
      }),
    []
  );

  const closeLanguage = useCallback(() => setIsLanguageOpen(false), []);
  const openLanguage = useCallback(() => {
    setIsMenuOpen(false);
    setIsLanguageOpen(true);
  }, []);

  const { categories } = useHomeCategories({ property, language });

  const availableLanguages = useMemo(
    () => [...(property.additionalLanguages ?? []), property.defaultLanguage],
    [property.additionalLanguages, property.defaultLanguage]
  );

  const languageItems = useLanguagePickerItems({
    availableLanguages,
    currentLanguage: language,
  });
  const showLanguagePicker = languageItems.length > 1;

  const announcement = useActiveAnnouncement({
    slug: property.slug,
    language,
    fallbackImageUrl: property.homepage?.heroBackgroundImage?.url,
  });

  const bannerConfig: AppShellBannerConfig | undefined =
    announcement && announcement.type !== 'dialog' && !isMinimalStyle
      ? {
          heading: announcement.heading,
          body: announcement.body,
          dismissible: announcement.dismissible,
          onDismiss: announcement.dismissible ? announcement.onDismiss : undefined,
          closeLabel: t('journeypicker.close'),
        }
      : undefined;

  const announcementConfig: AppShellAnnouncementConfig | undefined =
    announcement && announcement.type === 'dialog' && !isMinimalStyle
      ? {
          open: announcement.open,
          onClose: announcement.onClose,
          heading: announcement.heading,
          body: announcement.body,
          imageUrl: announcement.imageUrl,
          validFor: announcement.validFor,
          closeLabel: t('journeypicker.close'),
        }
      : undefined;

  const menuItems: MenuDrawerItem[] = useMemo(
    () =>
      categories.map((cat) => ({
        id: cat.id,
        label: cat.label,
        imageUrl: cat.imageUrl ?? null,
        onSelect: () => {
          closeMenu();
          const route = getCategoryListRoute(cat.id);
          if (route) router.push({ pathname: route });
        },
      })),
    [categories, closeMenu, router]
  );

  const navItems: SideNavItem[] = useMemo(
    () => [
      { id: 'home', label: t('nav.home'), icon: 'home', onClick: goHome },
      {
        id: 'menu',
        label: t('basket.menu'),
        icon: 'menu',
        active: isMenuOpen,
        onClick: toggleMenu,
      },
      {
        id: 'basket',
        label: t('app.basket'),
        icon: 'basket',
        badge: basketCount,
        onClick: goBasket,
      },
    ],
    [goHome, toggleMenu, isMenuOpen, goBasket, basketCount, t]
  );

  const bottomNavItems: SideNavItem[] = useMemo(
    () => [
      {
        id: 'account',
        label: guest ? t('nav.account') : t('common.signIn'),
        icon: 'account',
        onClick: goAccount,
        hideLabel: true,
      },
      ...(showLanguagePicker
        ? [
            {
              id: 'language',
              label: t('nav.language'),
              icon: 'language' as const,
              active: isLanguageOpen,
              onClick: openLanguage,
              hideLabel: true,
            },
          ]
        : []),
    ],
    [goAccount, guest, t, showLanguagePicker, isLanguageOpen, openLanguage]
  );

  const mobileNavItems: MobileTabItem[] = useMemo(
    () => [
      { id: 'home', label: t('nav.home'), icon: 'home', onClick: goHome },
      {
        id: 'menu',
        label: t('basket.menu'),
        icon: 'menu',
        active: isMenuOpen,
        onClick: toggleMenu,
      },
      {
        id: 'basket',
        label: t('app.basket'),
        icon: 'basket',
        badge: basketCount,
        onClick: goBasket,
      },
      {
        id: 'account',
        label: guest ? t('nav.account') : t('common.signIn'),
        icon: 'account',
        onClick: goAccount,
      },
    ],
    [goHome, toggleMenu, isMenuOpen, goBasket, basketCount, goAccount, guest, t]
  );

  const activeLanguageLabel = useMemo(
    () => languageItems.find((item) => item.active)?.name,
    [languageItems]
  );

  const siteFooter = (
    <FooterV2
      property={property}
      language={language}
      termsAndConditionPage={termsAndConditionPage}
      privacyAndCookiePolicyPage={privacyAndCookiePolicyPage}
      cancellationPage={cancellationPage}
    />
  );

  return (
    <ShellWrapper>
      <VoyagerThemeBridge />
      <AppShell
        navItems={navItems}
        bottomNavItems={bottomNavItems}
        mobileNavItems={mobileNavItems}
        navigationLabel={t('aria.mainNavigation')}
        banner={bannerConfig}
        announcement={announcementConfig}
        menu={{
          open: isMenuOpen,
          onClose: closeMenu,
          title: t('basket.menu'),
          closeLabel: t('journeypicker.close'),
          items: menuItems,
          width: '400px',
          noImageLabel: t('basket.noImageLabel'),
          languageSelector:
            showLanguagePicker && activeLanguageLabel
              ? {
                  label: activeLanguageLabel,
                  active: isLanguageOpen,
                  onSelect: openLanguage,
                }
              : undefined,
        }}
        languagePicker={
          showLanguagePicker
            ? {
                open: isLanguageOpen,
                onClose: closeLanguage,
                title: t('languageSwitcher.heading'),
                closeLabel: t('aria.languageSwitcher'),
                items: languageItems,
              }
            : undefined
        }
        footer={
          isMinimalStyle || isDiningBookingRoute || isRoomRateBookingRoute ? undefined : siteFooter
        }
        hideNavigation={isMinimalStyle}
      >
        {!hideTopBar && (
          <TopBar>
            <Logo logo={property.logo} name={property.name} />
          </TopBar>
        )}

        {children}
      </AppShell>
    </ShellWrapper>
  );
}
